import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';
import { playQueueOptions } from '../../../utils/common';

import {
  audioPromptsAllOptionGet,
  queuesAdd,
  queuesUpdate
} from '../../../store/slices/tenantSlices/incomingSlice';

const AddEditQueueModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const [audioPromptOption, setAudioPromptOption] = useState([]);

  const initialValues = {
    name: '',
    status: 'true',
    welcome_prompt: '',
    waiting_prompt: '',
    play_queue_no: 'no',
    play_queue_no_freq: '',
    queue_drop_time: 60
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('name is required'),
    welcome_prompt: Yup.string().required('welcome prompt is required'),
    waiting_prompt: Yup.string().required('waiting prompt is required'),
    play_queue_no_freq: Yup.string().required('play queue frequency is required'),
    queue_drop_time: Yup.string().required('queue drop time is required')
  });

  const getAudioPromptOption = async () => {
    try {
      const response = await dispatch(audioPromptsAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setAudioPromptOption(options);
      } else {
        setAudioPromptOption([]);
      }
    } catch (error) {
      setAudioPromptOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getAudioPromptOption();
  }, []);

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };
        const response = await dispatch(queuesUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        const response = await dispatch(queuesAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    if (isEdit) {
      setValues({
        ...editRecord,
        status: String(editRecord?.status),
        play_queue_no: editRecord?.play_queue_no ? 'yes' : 'no'
      });
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Queues' : 'Add Queues'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="queues" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="welcome_prompt"
                label="Welcome Prompt"
                placeholder="Select Welcome Prompt"
                value={values.welcome_prompt}
                options={audioPromptOption}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="waiting_prompt"
                label="Waiting Prompt"
                placeholder="Select Waiting Prompt"
                value={values.waiting_prompt}
                options={audioPromptOption}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                trueName="Yes"
                falseName="No"
                name="play_queue_no"
                label="Play Queue"
                value={String(values.play_queue_no === 'yes')}
                onChange={() =>
                  setFieldValue('play_queue_no', values.play_queue_no === 'yes' ? 'no' : 'yes')
                }
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="play_queue_no_freq"
                label="Play Queue Frequency"
                placeholder="Select Queue Frequency"
                value={values.play_queue_no_freq}
                options={playQueueOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            {/* <Col lg="3">
              <FormSelect
                name="queue_drop_time"
                label="Queue Drop Time"
                placeholder="Select Queue Drop Time"
                value={values.queue_drop_time}
                options={queueDropTimeOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col> */}
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="queues" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditQueueModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditQueueModal;
